// Informe Gerencial del coordinador (Fase A — Robotpack 2026-09).
//
// PDF A4 APAISADO real (páginas 842×595 pt, canvas a scale 2 vía lib/pdf.ts)
// siguiendo el mockup HTML aprobado: portada (wordmark, período, índice,
// "Solo información validada") + UNA PÁGINA POR SECCIÓN seleccionada. Motor
// simple de composición: cada sección es una función que dibuja su página con
// las primitivas compartidas de lib/informeCanvas.ts — sin flujo dinámico en
// v1 (decisión del brief).
//
// Convenciones OBLIGATORIAS del informe:
//   - Toda cifra visible lleva etiqueta.
//   - Toda tabla/serie lleva fila/columna Total.
//   - Semáforo 90/70 en los %.
//   - Coma decimal es-CO.
//   - Meta de facturación como línea/valor si está configurada.
//   - Deltas del comparativo en ok/crit.

import { inicioSemanaOperativaDeKey } from 'schemas';
import { descargarPdf } from '../../lib/pdf';
import {
  cargarFuentes,
  copM,
  drawBarrasDuales,
  drawBarrasH,
  drawWordmark,
  horasFmt,
  miles,
  MONO,
  P,
  roundRect,
  SANS,
  SERIF,
  type AreaDibujo,
} from '../../lib/informeCanvas';
import {
  agruparFacturacionPorMes,
  resumenCostosInforme,
  SECCIONES_INFORME,
  type DatosInformeGerencial,
  type SeccionInforme,
} from './informeGerencialDatos';

// ── Página A4 apaisada (pt = px CSS del canvas; scale 2 interno) ─────────────
const PW = 842;
const PH = 595;
const MX = 46;
const CW = PW - MX * 2;

const SCALE = 2;

// ── Formatters locales (coma decimal es-CO) ──────────────────────────────────
const pct0 = (n: number | null): string => (n === null ? '—' : `${Math.round(n)}%`);
const num1 = (n: number): string =>
  n.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const deltaPp = (n: number): string => `${n >= 0 ? '+' : '−'}${Math.abs(Math.round(n))} pp`;
const deltaPct = (n: number): string => `${n >= 0 ? '+' : '−'}${num1(Math.abs(n))}%`;

/** Semáforo unificado 90/70 de la plataforma. */
const colorSemaforo = (pct: number | null): string => {
  if (pct === null) return P.faint;
  if (pct >= 90) return P.ok;
  if (pct >= 70) return P.warn;
  return P.crit;
};

const MESES_CORTOS = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/**
 * Etiqueta de semana por FECHA DE INICIO ("Sem. del 1 jun") desde la
 * semana_iso que devuelve nivel-servicio TAL CUAL (lunes ISO clásico).
 * PENDIENTE con el cliente: si el informe debe renumerar o usar la semana
 * OPERATIVA configurable — hasta esa decisión NO se convierte ni renumera.
 */
function etiquetaSemana(semanaIso: string): string {
  try {
    const inicio = inicioSemanaOperativaDeKey(semanaIso, 1);
    const [, m, d] = inicio.split('-').map(Number);
    return `Sem. del ${d} ${MESES_CORTOS[(m ?? 1) - 1]}`;
  } catch {
    return semanaIso;
  }
}

function etiquetaMes(mesKey: string): string {
  const [y, m] = mesKey.split('-').map(Number);
  return `${MESES_CORTOS[(m ?? 1) - 1]} ${y}`;
}

// =============================================================================
// Infraestructura de página
// =============================================================================

interface Pagina {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

function crearPagina(): Pagina {
  const canvas = document.createElement('canvas');
  canvas.width = PW * SCALE;
  canvas.height = PH * SCALE;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(SCALE, SCALE);
  ctx.textBaseline = 'middle';
  ctx.fillStyle = P.surface;
  ctx.fillRect(0, 0, PW, PH);
  return { canvas, ctx };
}

/** Encabezado de página de sección. Devuelve el área de contenido. */
function drawHead(ctx: CanvasRenderingContext2D, titulo: string, ctxDerecha: string[]): AreaDibujo {
  ctx.textAlign = 'left';
  ctx.font = `26px ${SERIF}`;
  ctx.fillStyle = P.text;
  ctx.fillText(titulo, MX, 46);
  ctx.textAlign = 'right';
  ctx.font = `11px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctxDerecha.forEach((linea, i) => ctx.fillText(linea, PW - MX, 36 + i * 15));
  ctx.strokeStyle = P.border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(MX, 66);
  ctx.lineTo(PW - MX, 66);
  ctx.stroke();
  return { x: MX, y: 84, w: CW, h: PH - 84 - 44 };
}

function drawFoot(ctx: CanvasRenderingContext2D, pagina: number, total: number): void {
  const y = PH - 24;
  ctx.strokeStyle = P.border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(MX, y - 12);
  ctx.lineTo(PW - MX, y - 12);
  ctx.stroke();
  ctx.font = `600 10px ${SANS}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = P.ok;
  ctx.fillText('✓', MX, y);
  ctx.fillStyle = P.muted;
  ctx.fillText('Producción validada por supervisión', MX + 14, y);
  ctx.textAlign = 'right';
  ctx.font = `10px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.fillText(`Jidokap · Informe gerencial · pág. ${pagina} de ${total}`, PW - MX, y);
}

/** Card KPI con etiqueta, valor gordo y línea comparativa coloreada. */
function drawKpi(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  kpi: { lbl: string; val: string; valColor?: string; cmp?: string; cmpTone?: 'ok' | 'crit' },
): void {
  ctx.fillStyle = P.zebra;
  roundRect(ctx, x, y, w, h, 8);
  ctx.fill();
  ctx.strokeStyle = P.border;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.textAlign = 'left';
  ctx.font = `600 9px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.fillText(recortar(ctx, kpi.lbl.toUpperCase(), w - 28), x + 14, y + 18);
  ctx.font = `700 26px ${MONO}`;
  ctx.fillStyle = kpi.valColor ?? P.text;
  ctx.fillText(kpi.val, x + 14, y + h / 2 + 4);
  if (kpi.cmp) {
    ctx.font = `10px ${SANS}`;
    ctx.fillStyle = kpi.cmpTone === 'ok' ? P.ok : kpi.cmpTone === 'crit' ? P.crit : P.muted;
    // Nunca se sale de la tarjeta: se recorta con elipsis (antes pisaba la vecina).
    ctx.fillText(recortar(ctx, kpi.cmp, w - 28), x + 14, y + h - 16);
  }
}

/** Recorta con "…" para que el texto quepa en `maxW` con la fuente ACTUAL del ctx. */
function recortar(ctx: CanvasRenderingContext2D, texto: string, maxW: number): string {
  if (ctx.measureText(texto).width <= maxW) return texto;
  let t = texto;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

/** Parte `texto` en líneas que quepan en `maxW` con la fuente ACTUAL del ctx. */
function envolver(ctx: CanvasRenderingContext2D, texto: string, maxW: number): string[] {
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of texto.split(/\s+/)) {
    const candidata = actual ? `${actual} ${palabra}` : palabra;
    if (ctx.measureText(candidata).width <= maxW || !actual) actual = candidata;
    else {
      lineas.push(actual);
      actual = palabra;
    }
  }
  if (actual) lineas.push(actual);
  return lineas;
}

interface Celda {
  texto: string;
  color?: string;
  bold?: boolean;
}
interface ColTabla {
  titulo: string;
  w: number;
  align?: 'left' | 'right';
}

/** Tabla simple con encabezado, zebra y fila Total obligatoria (si se pasa). */
function drawTabla(
  ctx: CanvasRenderingContext2D,
  area: AreaDibujo,
  cols: ColTabla[],
  filas: Celda[][],
  totalRow: Celda[] | null,
  filaH = 22,
): number {
  const xs: number[] = [];
  let acc = area.x;
  for (const c of cols) {
    xs.push(acc);
    acc += c.w;
  }
  const celX = (i: number): number =>
    cols[i]!.align === 'right' ? xs[i]! + cols[i]!.w - 6 : xs[i]! + 2;
  // Header
  ctx.font = `600 8.5px ${SANS}`;
  ctx.fillStyle = P.faint;
  cols.forEach((c, i) => {
    ctx.textAlign = c.align === 'right' ? 'right' : 'left';
    ctx.fillText(c.titulo.toUpperCase(), celX(i), area.y + 8);
  });
  ctx.strokeStyle = P.border;
  ctx.beginPath();
  ctx.moveTo(area.x, area.y + 16);
  ctx.lineTo(area.x + area.w, area.y + 16);
  ctx.stroke();
  // Filas
  let y = area.y + 16;
  filas.forEach((fila, fi) => {
    if (fi % 2 === 1) {
      ctx.fillStyle = P.zebra;
      ctx.fillRect(area.x, y, area.w, filaH);
    }
    fila.forEach((cel, i) => {
      ctx.font = `${cel.bold ? '600 ' : ''}10.5px ${cols[i]!.align === 'right' ? MONO : SANS}`;
      ctx.fillStyle = cel.color ?? P.text;
      ctx.textAlign = cols[i]!.align === 'right' ? 'right' : 'left';
      ctx.fillText(cel.texto, celX(i), y + filaH / 2 + 1);
    });
    y += filaH;
  });
  // Total
  if (totalRow) {
    ctx.strokeStyle = P.text;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(area.x, y + 0.5);
    ctx.lineTo(area.x + area.w, y + 0.5);
    ctx.stroke();
    totalRow.forEach((cel, i) => {
      ctx.font = `600 10.5px ${cols[i]!.align === 'right' ? MONO : SANS}`;
      ctx.fillStyle = cel.color ?? P.text;
      ctx.textAlign = cols[i]!.align === 'right' ? 'right' : 'left';
      ctx.fillText(cel.texto, celX(i), y + filaH / 2 + 1);
    });
    y += filaH;
  }
  ctx.textAlign = 'left';
  return y;
}

/**
 * Nota al pie de sección. Envuelve hasta 2 líneas dentro del ancho útil (las
 * notas van a PH-56: la segunda línea queda a PH-44, por encima del pie).
 */
function drawNota(ctx: CanvasRenderingContext2D, x: number, y: number, texto: string): void {
  ctx.font = `9.5px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.textAlign = 'left';
  const maxW = PW - MX - x;
  const lineas = envolver(ctx, texto, maxW);
  if (lineas.length <= 2) {
    lineas.forEach((l, i) => ctx.fillText(l, x, y + i * 12));
    return;
  }
  ctx.fillText(lineas[0]!, x, y);
  ctx.fillText(recortar(ctx, lineas.slice(1).join(' '), maxW), x, y + 12);
}

// =============================================================================
// Input del generador
// =============================================================================

export interface InformeGerencialInput {
  tenantNombre: string;
  plantasLabel: string;
  periodoLabel: string;
  /** Ej. "vs. 1 may – 31 may 2026". Null = sin comparación. */
  compLabel: string | null;
  descargadoPor: string;
  /** Secciones seleccionadas CON datos (el llamador ya filtró las sin datos). */
  secciones: SeccionInforme[];
  totalDisponibles: number;
  datos: DatosInformeGerencial;
}

// =============================================================================
// Portada
// =============================================================================

function paginaPortada(input: InformeGerencialInput, totalPaginas: number): HTMLCanvasElement {
  const { canvas, ctx } = crearPagina();
  drawWordmark(ctx, MX, 44, 20, P.text);

  ctx.textAlign = 'left';
  ctx.font = `52px ${SERIF}`;
  ctx.fillStyle = P.text;
  ctx.fillText('Informe gerencial', MX, 150);
  ctx.fillText('de producción', MX, 204);
  ctx.font = `15px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.fillText(`${input.tenantNombre} · ${input.plantasLabel}`, MX, 244);
  ctx.font = `20px ${SERIF}`;
  ctx.fillStyle = P.text;
  ctx.fillText(input.periodoLabel, MX, 276);
  if (input.compLabel) {
    const w = ctx.measureText(input.periodoLabel).width;
    ctx.fillStyle = P.accent;
    ctx.fillText(`  · ${input.compLabel}`, MX + w, 276);
  }

  // Índice de secciones incluidas (grid 3 col, como el mockup).
  const colW = (CW - 2 * 12) / 3;
  const cardH = 56;
  input.secciones.forEach((id, i) => {
    const meta = SECCIONES_INFORME.find((s) => s.id === id)!;
    const cx = MX + (i % 3) * (colW + 12);
    const cy = 310 + Math.floor(i / 3) * (cardH + 10);
    ctx.fillStyle = P.zebra;
    roundRect(ctx, cx, cy, colW, cardH, 6);
    ctx.fill();
    ctx.strokeStyle = P.border;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.font = `9px ${MONO}`;
    ctx.fillStyle = P.accent;
    ctx.fillText(String(i + 1).padStart(2, '0'), cx + 12, cy + 15);
    ctx.font = `600 11.5px ${SANS}`;
    ctx.fillStyle = P.text;
    ctx.fillText(meta.titulo, cx + 12, cy + 30);
    ctx.font = `9px ${SANS}`;
    ctx.fillStyle = P.muted;
    const detalle = meta.detalle.length > 52 ? `${meta.detalle.slice(0, 51)}…` : meta.detalle;
    ctx.fillText(detalle, cx + 12, cy + 44);
  });

  const generado = new Date().toLocaleDateString('es-CO', { dateStyle: 'long' });
  ctx.font = `10.5px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.fillText(
    `Generado desde JIDOKAP el ${generado} · Solicitado por: ${input.descargadoPor}`,
    MX,
    PH - 66,
  );
  ctx.fillText(
    `Indicadores seleccionados: ${input.secciones.length} de ${input.totalDisponibles} disponibles · Producción y facturación: validada por supervisión · Paros, Pits y personal: registro operativo`,
    MX,
    PH - 50,
  );
  drawFoot(ctx, 1, totalPaginas);
  return canvas;
}

// =============================================================================
// Secciones (una función = una página)
// =============================================================================

type DibujaSeccion = (
  input: InformeGerencialInput,
  numero: string,
  pagina: number,
  total: number,
) => HTMLCanvasElement;

const seccionResumen: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Resumen ejecutivo`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    input.compLabel ? `${input.periodoLabel} · ${input.compLabel}` : input.periodoLabel,
  ]);

  const cAct = d.cumplimiento.ok ? d.cumplimiento.data : null;
  const cAnt = d.cumplimientoComp?.ok ? d.cumplimientoComp.data : null;
  const pAct = d.paros.ok ? d.paros.data : null;
  const pAnt = d.parosComp?.ok ? d.parosComp.data : null;
  const nAct = d.nivel.ok ? d.nivel.data : null;

  const kpis: Array<Parameters<typeof drawKpi>[5]> = [];
  if (cAct) {
    const real = cAct.totales.cantidad_real_total;
    const realAnt = cAnt?.totales.cantidad_real_total ?? null;
    const dReal = realAnt !== null && realAnt > 0 ? ((real - realAnt) / realAnt) * 100 : null;
    kpis.push({
      lbl: 'Unidades producidas',
      val: miles(real),
      cmp:
        realAnt !== null
          ? `ant. ${miles(realAnt)} → ${miles(real)} · ${dReal === null ? '—' : deltaPct(dReal)}`
          : 'unidades validadas del período',
      cmpTone: dReal === null ? undefined : dReal >= 0 ? 'ok' : 'crit',
    });
    const meta = cAct.totales.cumplimiento_meta_promedio_pct;
    const metaAnt = cAnt?.totales.cumplimiento_meta_promedio_pct ?? null;
    kpis.push({
      lbl: 'Cumplimiento de meta (capacidad)',
      val: pct0(meta),
      valColor: colorSemaforo(meta),
      cmp:
        metaAnt !== null && meta !== null
          ? `ant. ${pct0(metaAnt)} → ${pct0(meta)} · ${deltaPp(meta - metaAnt)}`
          : 'real / capacidad instalada',
      cmpTone: metaAnt !== null && meta !== null ? (meta >= metaAnt ? 'ok' : 'crit') : undefined,
    });
    const prog = cAct.totales.cumplimiento_promedio_pct;
    const progAnt = cAnt?.totales.cumplimiento_promedio_pct ?? null;
    kpis.push({
      lbl: 'Cumplimiento del programa',
      val: pct0(prog),
      valColor: colorSemaforo(prog),
      cmp:
        progAnt !== null && prog !== null
          ? `ant. ${pct0(progAnt)} → ${pct0(prog)} · ${deltaPp(prog - progAnt)}`
          : 'real / programa vigente del cliente',
      cmpTone: progAnt !== null && prog !== null ? (prog >= progAnt ? 'ok' : 'crit') : undefined,
    });
  }
  if (pAct) {
    const horas = pAct.totales.horas_totales;
    const horasAnt = pAnt?.totales.horas_totales ?? null;
    const dHoras = horasAnt !== null && horasAnt > 0 ? ((horas - horasAnt) / horasAnt) * 100 : null;
    // B2 "paro total registrado" (diccionario v1): TODOS los paros cerrados,
    // incluidos programados. Distinto (y con otro nombre) del "paro que afecta
    // disponibilidad" del Dashboard. Los abiertos van aparte (DN1).
    const enCurso = pAct.totales.en_curso ?? 0;
    kpis.push({
      lbl: 'Paro total (incl. programados)',
      val: horasFmt(horas * 60),
      cmp:
        horasAnt !== null
          ? `ant. ${horasFmt(horasAnt * 60)} → ${horasFmt(horas * 60)} · ${dHoras === null ? '—' : deltaPct(dHoras)}`
          : `${miles(pAct.totales.paros_count)} eventos cerrados${enCurso > 0 ? ` · ${enCurso} en curso aparte` : ''}`,
      // Menos paro = mejor.
      cmpTone: dHoras === null ? undefined : dHoras <= 0 ? 'ok' : 'crit',
    });
  }
  if (nAct) {
    kpis.push({
      lbl: 'Nivel de servicio real',
      val: pct0(nAct.acumulado.cumplimiento_real_pct),
      valColor: colorSemaforo(nAct.acumulado.cumplimiento_real_pct),
      cmp: `cumplimiento ácido ${pct0(nAct.acumulado.cumplimiento_pct)} · acumulado del período`,
    });
  }
  if (cAct && cAct.totales.utilidad_total !== null && cAct.totales.utilidad_total !== undefined) {
    const uAnt = cAnt?.totales.utilidad_total ?? null;
    const dU =
      uAnt !== null && uAnt !== undefined && uAnt !== 0
        ? ((cAct.totales.utilidad_total - uAnt) / Math.abs(uAnt)) * 100
        : null;
    kpis.push({
      lbl: 'Utilidad estimada',
      val: copM(cAct.totales.utilidad_total),
      cmp:
        uAnt !== null && uAnt !== undefined
          ? `ant. ${copM(uAnt)} → ${copM(cAct.totales.utilidad_total)} · ${dU === null ? '—' : deltaPct(dU)}`
          : 'valor producido − mano de obra',
      cmpTone: dU === null ? undefined : dU >= 0 ? 'ok' : 'crit',
    });
  }

  const kpiW = (area.w - 2 * 14) / 3;
  const kpiH = 84;
  kpis.slice(0, 6).forEach((k, i) => {
    drawKpi(
      ctx,
      area.x + (i % 3) * (kpiW + 14),
      area.y + Math.floor(i / 3) * (kpiH + 12),
      kpiW,
      kpiH,
      k,
    );
  });

  // Observaciones del período — HECHOS calculables, nunca recomendaciones.
  const obs: string[] = [];
  if (pAct && pAct.por_causal_especifica.length > 0) {
    const top = pAct.por_causal_especifica[0]!;
    obs.push(
      `La causal «${top.causal_nombre}» concentra el ${num1(top.pct_minutos)}% de los minutos perdidos del período.`,
    );
  }
  if (pAct && pAnt && pAnt.totales.horas_totales > 0) {
    const delta =
      ((pAct.totales.horas_totales - pAnt.totales.horas_totales) / pAnt.totales.horas_totales) *
      100;
    obs.push(
      `El tiempo de paro ${delta <= 0 ? 'bajó' : 'subió'} ${num1(Math.abs(delta))}% respecto al período de comparación.`,
    );
  }
  if (cAct && cAnt) {
    const areasAct = agruparAreas(cAct);
    const areasAnt = agruparAreas(cAnt);
    let mejoraron = 0;
    let evaluadas = 0;
    for (const [nombre, a] of areasAct) {
      const b = areasAnt.get(nombre);
      if (!b || a.pctMeta === null || b.pctMeta === null) continue;
      evaluadas++;
      if (a.pctMeta > b.pctMeta) mejoraron++;
    }
    if (evaluadas > 0) {
      obs.push(
        `${mejoraron} de ${evaluadas} áreas mejoraron su cumplimiento de meta respecto al período de comparación.`,
      );
    }
  }
  if (obs.length > 0) {
    const oy = area.y + 2 * (kpiH + 12) + 8;
    ctx.fillStyle = P.zebra;
    roundRect(ctx, area.x, oy, area.w, 24 + obs.length * 20 + 8, 6);
    ctx.fill();
    ctx.fillStyle = P.accent;
    ctx.fillRect(area.x, oy, 3, 24 + obs.length * 20 + 8);
    ctx.font = `600 9px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.textAlign = 'left';
    ctx.fillText('OBSERVACIONES DEL PERÍODO', area.x + 16, oy + 16);
    ctx.font = `11px ${SANS}`;
    obs.forEach((o, i) => {
      ctx.fillStyle = P.accent;
      ctx.fillText('•', area.x + 16, oy + 36 + i * 20);
      ctx.fillStyle = P.text;
      ctx.fillText(o, area.x + 28, oy + 36 + i * 20);
    });
  }

  drawFoot(ctx, pagina, total);
  return canvas;
};

/** Agrega las filas de cumplimiento (agrupacion=area) por área. */
function agruparAreas(c: {
  filas: Array<{
    area_nombre: string | null;
    cantidad_real: number;
    meta_acumulada: number;
    // null con filtro de línea (V2) — el informe no filtra por línea, pero el
    // contrato del endpoint ahora lo permite.
    cantidad_programa: number | null;
  }>;
}): Map<string, { real: number; meta: number; programa: number; pctMeta: number | null }> {
  const m = new Map<string, { real: number; meta: number; programa: number }>();
  for (const f of c.filas) {
    const k = f.area_nombre ?? 'Sin área';
    const acc = m.get(k) ?? { real: 0, meta: 0, programa: 0 };
    acc.real += f.cantidad_real;
    acc.meta += f.meta_acumulada;
    acc.programa += f.cantidad_programa ?? 0;
    m.set(k, acc);
  }
  return new Map(
    [...m.entries()].map(([k, v]) => [
      k,
      { ...v, pctMeta: v.meta > 0 ? (v.real / v.meta) * 100 : null },
    ]),
  );
}

const seccionFacturacion: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Facturación`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    input.compLabel
      ? `${input.periodoLabel} · ${input.compLabel}`
      : `${input.periodoLabel} · producción validada facturada por Q`,
  ]);
  if (!d.facturacion.ok) {
    drawNota(ctx, area.x, area.y + 20, 'Sin datos de facturación para el período.');
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const f = d.facturacion.data;
  const fAnt = d.facturacionComp?.ok ? d.facturacionComp.data : null;
  const metaM = f.meta_facturacion_mensual_millones;

  // KPIs — SIEMPRE del período del informe; la comparación va como "ant. → act."
  // (antes el KPI sumaba período + comparación y cambiaba al activar comparar).
  const kpiW = (area.w - 2 * 14) / 3;
  const dFact =
    fAnt && fAnt.totales.valor_total > 0
      ? ((f.totales.valor_total - fAnt.totales.valor_total) / fAnt.totales.valor_total) * 100
      : null;
  drawKpi(ctx, area.x, area.y, kpiW, 76, {
    lbl: 'Valor total facturado',
    val: copM(f.totales.valor_total),
    cmp: fAnt
      ? `ant. ${copM(fAnt.totales.valor_total)} → ${copM(f.totales.valor_total)} · ${dFact === null ? '—' : deltaPct(dFact)}`
      : `${miles(f.totales.ops)} OPs · ${miles(f.totales.unidades_validadas)} unidades validadas`,
    cmpTone: dFact === null ? undefined : dFact >= 0 ? 'ok' : 'crit',
  });
  drawKpi(ctx, area.x + kpiW + 14, area.y, kpiW, 76, {
    lbl: 'Valor dominical',
    val: copM(f.totales.valor_dominical),
    cmp: `semanal ${copM(f.totales.valor_semanal)} · recargo ×1,8 con switch de OP`,
  });
  drawKpi(ctx, area.x + 2 * (kpiW + 14), area.y, kpiW, 76, {
    lbl: 'Meta mensual configurada',
    val: metaM === null ? '—' : `$${num1(metaM)} M`,
    cmp:
      metaM === null
        ? 'sin meta en Configuración del Tenant (A1)'
        : 'meta de facturación mensual (A1)',
  });

  // Agrupar por MES (q_key = 'YYYY-MM-Qn') y por ÁREA, agregación en FE. Los
  // meses del período de comparación van en filas PROPIAS marcadas "(comp.)"
  // y NO suman al total (que es el del período del informe): dos semanas del
  // mismo mes producen dos filas, no una sumada.
  const mesesAgrupados = agruparFacturacionPorMes(f.filas, fAnt ? fAnt.filas : null);
  const porArea = new Map<string, { unidades: number; valor: number }>();
  for (const ra of f.resumen_areas) {
    const acc = porArea.get(ra.area_nombre) ?? { unidades: 0, valor: 0 };
    acc.unidades += ra.unidades_validadas;
    acc.valor += ra.valor_total;
    porArea.set(ra.area_nombre, acc);
  }

  const ty = area.y + 96;
  const halfW = (area.w - 24) / 2;
  ctx.font = `600 10px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.textAlign = 'left';
  ctx.fillText('POR MES (VS META SI ESTÁ CONFIGURADA)', area.x, ty);
  const metaPesos = metaM === null ? null : metaM * 1_000_000;
  const filasMes: Celda[][] = mesesAgrupados.map((v) => {
    const mes = v.mes;
    const vsMeta = metaPesos !== null && metaPesos > 0 ? (v.valor / metaPesos) * 100 : null;
    const tinta = v.comp ? P.muted : undefined;
    return [
      { texto: v.comp ? `${etiquetaMes(mes)} (comp.)` : etiquetaMes(mes), color: tinta },
      { texto: miles(v.unidades), color: tinta },
      { texto: copM(v.valor), color: tinta },
      {
        texto: vsMeta === null ? '—' : pct0(vsMeta),
        color: vsMeta === null ? P.faint : v.comp ? P.muted : colorSemaforo(vsMeta),
        bold: vsMeta !== null && !v.comp,
      },
    ];
  });
  drawTabla(
    ctx,
    { x: area.x, y: ty + 8, w: halfW, h: 0 },
    [
      { titulo: 'Mes', w: halfW * 0.3 },
      { titulo: 'Unidades', w: halfW * 0.25, align: 'right' },
      { titulo: 'Valor', w: halfW * 0.25, align: 'right' },
      { titulo: 'vs meta', w: halfW * 0.2, align: 'right' },
    ],
    filasMes,
    [
      { texto: fAnt ? 'Total del período' : 'Total', bold: true },
      { texto: miles(f.totales.unidades_validadas), bold: true },
      { texto: copM(f.totales.valor_total), bold: true },
      { texto: '', bold: true },
    ],
  );

  ctx.font = `600 10px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.fillText('POR ÁREA (PERÍODO DEL INFORME)', area.x + halfW + 24, ty);
  const areasOrdenadas = [...porArea.entries()].sort((a, b) => b[1].valor - a[1].valor);
  drawTabla(
    ctx,
    { x: area.x + halfW + 24, y: ty + 8, w: halfW, h: 0 },
    [
      { titulo: 'Área', w: halfW * 0.4 },
      { titulo: 'Unidades', w: halfW * 0.3, align: 'right' },
      { titulo: 'Valor', w: halfW * 0.3, align: 'right' },
    ],
    areasOrdenadas.map(([nombre, v]) => [
      { texto: nombre },
      { texto: miles(v.unidades) },
      { texto: copM(v.valor) },
    ]),
    [
      { texto: 'Total', bold: true },
      { texto: miles(areasOrdenadas.reduce((s, [, v]) => s + v.unidades, 0)), bold: true },
      { texto: copM(areasOrdenadas.reduce((s, [, v]) => s + v.valor, 0)), bold: true },
    ],
  );

  const notaY = PH - 56;
  drawNota(
    ctx,
    area.x,
    notaY,
    `Solo OPs terminadas con producción validada, facturadas en el Q de su cierre.${
      fAnt ? ' Los meses "(comp.)" son del período de comparación y no suman al total.' : ''
    }${
      f.totales.filas_sin_precio > 0
        ? ` ${f.totales.filas_sin_precio} grupo(s) sin precio vigente facturan $0 — cargue el precio en C10.`
        : ''
    }`,
  );
  drawFoot(ctx, pagina, total);
  return canvas;
};

const seccionNivel: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Nivel de servicio`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    'unidades programadas vs realizadas por semana',
  ]);
  if (!d.nivel.ok) {
    drawNota(ctx, area.x, area.y + 20, 'Sin datos de nivel de servicio para el período.');
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const n = d.nivel.data;
  const semanas = n.semanas.slice(-12); // cap visual (12 semanas ≈ trimestre)

  drawBarrasDuales(
    ctx,
    { x: area.x, y: area.y + 18, w: area.w, h: 190 },
    semanas.map((s) => ({
      label: etiquetaSemana(s.semana_iso),
      programada: s.programadas,
      real: s.realizadas,
    })),
    ['Programado', 'Realizado'],
  );

  // Tabla resumen con fila Total (acumulado del período).
  const ty = area.y + 232;
  const cols: ColTabla[] = [
    { titulo: 'Semana', w: area.w * 0.2 },
    { titulo: 'Programadas', w: area.w * 0.17, align: 'right' },
    { titulo: 'Realizadas', w: area.w * 0.17, align: 'right' },
    { titulo: 'No entreg. (cliente)', w: area.w * 0.16, align: 'right' },
    { titulo: 'Cumpl. ácido', w: area.w * 0.15, align: 'right' },
    { titulo: 'Cumpl. real', w: area.w * 0.15, align: 'right' },
  ];
  drawTabla(
    ctx,
    { x: area.x, y: ty, w: area.w, h: 0 },
    cols,
    semanas.map((s) => [
      { texto: etiquetaSemana(s.semana_iso) },
      { texto: miles(s.programadas) },
      { texto: miles(s.realizadas) },
      { texto: miles(s.no_entregadas) },
      { texto: pct0(s.cumplimiento_pct), color: colorSemaforo(s.cumplimiento_pct) },
      {
        texto: pct0(s.cumplimiento_real_pct),
        color: colorSemaforo(s.cumplimiento_real_pct),
        bold: true,
      },
    ]),
    [
      { texto: 'Total', bold: true },
      { texto: miles(n.acumulado.programadas), bold: true },
      { texto: miles(n.acumulado.realizadas), bold: true },
      { texto: miles(n.acumulado.no_entregadas), bold: true },
      {
        texto: pct0(n.acumulado.cumplimiento_pct),
        color: colorSemaforo(n.acumulado.cumplimiento_pct),
        bold: true,
      },
      {
        texto: pct0(n.acumulado.cumplimiento_real_pct),
        color: colorSemaforo(n.acumulado.cumplimiento_real_pct),
        bold: true,
      },
    ],
    16,
  );
  drawNota(
    ctx,
    area.x,
    PH - 56,
    'Cumplimiento real = (realizadas + no entregadas por el cliente) / programadas. Semanas etiquetadas por su fecha de inicio.',
  );
  drawFoot(ctx, pagina, total);
  return canvas;
};

const seccionProductividad: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Productividad por área`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    'cumplimiento de meta (capacidad) por área',
  ]);
  if (!d.cumplimiento.ok) {
    drawNota(ctx, area.x, area.y + 20, 'Sin datos de cumplimiento para el período.');
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const act = agruparAreas(d.cumplimiento.data);
  const ant = d.cumplimientoComp?.ok ? agruparAreas(d.cumplimientoComp.data) : null;
  const nombres = [...act.keys()].sort();

  drawBarrasDuales(
    ctx,
    { x: area.x, y: area.y + 18, w: area.w * 0.56, h: 220 },
    nombres.map((nombre) => ({
      label: nombre.length > 12 ? `${nombre.slice(0, 11)}…` : nombre,
      programada: ant?.get(nombre)?.pctMeta ?? null,
      real: act.get(nombre)?.pctMeta ?? 0,
    })),
    [input.compLabel ? 'Período anterior (%)' : 'Sin comparación', 'Período actual (%)'],
  );

  // Tabla detalle con Δpp y fila Planta (total ponderado real/meta).
  const tx = area.x + area.w * 0.6;
  const tw = area.w * 0.4;
  ctx.font = `600 10px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.textAlign = 'left';
  ctx.fillText('DETALLE POR ÁREA (% CUMPLIMIENTO DE META)', tx, area.y + 8);
  let totalReal = 0;
  let totalMeta = 0;
  let totalRealAnt = 0;
  let totalMetaAnt = 0;
  const filas: Celda[][] = nombres.map((nombre) => {
    const a = act.get(nombre)!;
    totalReal += a.real;
    totalMeta += a.meta;
    const b = ant?.get(nombre) ?? null;
    if (b) {
      totalRealAnt += b.real;
      totalMetaAnt += b.meta;
    }
    const delta = a.pctMeta !== null && b?.pctMeta != null ? a.pctMeta - b.pctMeta : null;
    return [
      { texto: nombre },
      { texto: b ? pct0(b.pctMeta) : '—', color: P.muted },
      { texto: pct0(a.pctMeta), color: colorSemaforo(a.pctMeta), bold: true },
      {
        texto: delta === null ? '—' : deltaPp(delta),
        color: delta === null ? P.faint : delta >= 0 ? P.ok : P.crit,
        bold: delta !== null,
      },
    ];
  });
  const pctTotal = totalMeta > 0 ? (totalReal / totalMeta) * 100 : null;
  const pctTotalAnt = totalMetaAnt > 0 ? (totalRealAnt / totalMetaAnt) * 100 : null;
  const deltaTotal = pctTotal !== null && pctTotalAnt !== null ? pctTotal - pctTotalAnt : null;
  drawTabla(
    ctx,
    { x: tx, y: area.y + 16, w: tw, h: 0 },
    [
      { titulo: 'Área', w: tw * 0.4 },
      { titulo: 'Anterior', w: tw * 0.2, align: 'right' },
      { titulo: 'Actual', w: tw * 0.2, align: 'right' },
      { titulo: 'Δ pp', w: tw * 0.2, align: 'right' },
    ],
    filas,
    [
      { texto: 'Planta', bold: true },
      { texto: pct0(pctTotalAnt), bold: true },
      { texto: pct0(pctTotal), color: colorSemaforo(pctTotal), bold: true },
      {
        texto: deltaTotal === null ? '—' : deltaPp(deltaTotal),
        color: deltaTotal === null ? P.faint : deltaTotal >= 0 ? P.ok : P.crit,
        bold: true,
      },
    ],
    20,
  );
  drawNota(
    ctx,
    area.x,
    PH - 56,
    'Cumplimiento de meta = producción real / capacidad instalada (SKU × línea × horas capturadas). Semáforo: verde ≥ 90 · ámbar ≥ 70 · rojo < 70.',
  );
  drawFoot(ctx, pagina, total);
  return canvas;
};

const seccionLideres: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Cumplimiento de líderes`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    'producido vs esperado (capacidad) por líder',
  ]);
  if (!d.personal.ok) {
    drawNota(ctx, area.x, area.y + 20, 'Sin datos de personal para el período.');
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const lideres = d.personal.data.lideres
    .filter((l) => l.bloques_capturados > 0)
    .sort((a, b) => (b.cumplimiento_meta_pct ?? -1) - (a.cumplimiento_meta_pct ?? -1));
  const top = lideres.slice(0, 14);

  const cols: ColTabla[] = [
    { titulo: '#', w: area.w * 0.05 },
    { titulo: 'Líder', w: area.w * 0.28 },
    { titulo: 'Línea', w: area.w * 0.1 },
    { titulo: 'Producido', w: area.w * 0.14, align: 'right' },
    { titulo: 'Esperado', w: area.w * 0.14, align: 'right' },
    { titulo: 'Cumplimiento (barra)', w: area.w * 0.17 },
    { titulo: '%', w: area.w * 0.12, align: 'right' },
  ];
  const filaH = 24;
  const filas: Celda[][] = top.map((l, i) => [
    { texto: String(i + 1) },
    { texto: `${l.nombre} ${l.apellido}` },
    { texto: l.linea_codigo ?? '—' },
    { texto: miles(l.cantidad_real) },
    { texto: miles(l.esperado_capacidad) },
    { texto: '' }, // barra dibujada aparte
    {
      texto: pct0(l.cumplimiento_meta_pct),
      color: colorSemaforo(l.cumplimiento_meta_pct),
      bold: true,
    },
  ]);
  const totalProducido = lideres.reduce((s, l) => s + l.cantidad_real, 0);
  const totalEsperado = lideres.reduce((s, l) => s + l.esperado_capacidad, 0);
  const pctTotal = totalEsperado > 0 ? (totalProducido / totalEsperado) * 100 : null;
  drawTabla(
    ctx,
    { x: area.x, y: area.y + 4, w: area.w, h: 0 },
    cols,
    filas,
    [
      { texto: '', bold: true },
      { texto: `Total (${lideres.length} líderes)`, bold: true },
      { texto: '' },
      { texto: miles(totalProducido), bold: true },
      { texto: miles(totalEsperado), bold: true },
      { texto: '' },
      { texto: pct0(pctTotal), color: colorSemaforo(pctTotal), bold: true },
    ],
    filaH,
  );
  // Barras de cumplimiento sobre la columna reservada.
  const barX = area.x + area.w * 0.71 + 4;
  const barW = area.w * 0.17 - 16;
  top.forEach((l, i) => {
    const y = area.y + 4 + 16 + i * filaH + filaH / 2;
    const pct = l.cumplimiento_meta_pct;
    ctx.fillStyle = P.barTrack;
    roundRect(ctx, barX, y - 4, barW, 8, 4);
    ctx.fill();
    if (pct !== null && pct > 0) {
      ctx.fillStyle = colorSemaforo(pct);
      roundRect(ctx, barX, y - 4, Math.min(barW, (Math.min(pct, 100) / 100) * barW), 8, 4);
      ctx.fill();
    }
  });
  if (lideres.length > top.length) {
    drawNota(
      ctx,
      area.x,
      area.y + 4 + 16 + (top.length + 1) * filaH + 14,
      `+${lideres.length - top.length} líderes más con captura en el período (fuera del top mostrado).`,
    );
  }
  drawNota(
    ctx,
    area.x,
    PH - 56,
    'Solo producción validada · % = producido / esperado según capacidad y horas asignadas.',
  );
  drawFoot(ctx, pagina, total);
  return canvas;
};

const seccionParos: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Análisis de paros`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    'paro total registrado por causal · pérdida $ solo donde afecta disponibilidad',
  ]);
  if (!d.paros.ok) {
    drawNota(ctx, area.x, area.y + 20, 'Sin datos de paros para el período.');
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const p = d.paros.data;
  const pAnt = d.parosComp?.ok ? d.parosComp.data : null;
  const lineasOk = d.lineas.ok ? d.lineas.data : null;
  const causales = p.por_causal_especifica.slice(0, 10);

  // KPIs arriba.
  const kpiW = (area.w - 2 * 14) / 3;
  // B2 paro total registrado (incluye programados), solo cerrados; DN1 aparte.
  const enCurso = p.totales.en_curso ?? 0;
  drawKpi(ctx, area.x, area.y, kpiW, 70, {
    lbl: 'Paro total (incl. programados)',
    val: horasFmt(p.totales.minutos_totales),
    cmp: pAnt
      ? `ant. ${horasFmt(pAnt.totales.minutos_totales)} → ${horasFmt(p.totales.minutos_totales)}`
      : `solo paros cerrados${enCurso > 0 ? ` · ${enCurso} en curso aparte` : ''}`,
    cmpTone: pAnt
      ? p.totales.minutos_totales <= pAnt.totales.minutos_totales
        ? 'ok'
        : 'crit'
      : undefined,
  });
  drawKpi(ctx, area.x + kpiW + 14, area.y, kpiW, 70, {
    lbl: 'Eventos cerrados',
    val: miles(p.totales.paros_count),
    cmp: `${p.totales.dias_con_paros} día(s) con paros en el período`,
  });
  const perdidaTotal = lineasOk?.totales.perdida_pesos_total ?? null;
  // Es un valor, no una alarma: sin rojo (política de color 3 capas).
  drawKpi(ctx, area.x + 2 * (kpiW + 14), area.y, kpiW, 70, {
    lbl: 'Pérdida por paros (valor no producido)',
    val: perdidaTotal === null || perdidaTotal === undefined ? '—' : copM(perdidaTotal),
    cmp: 'min × capacidad × precio SKU · solo si afecta disponibilidad',
  });

  // Izquierda: barras H de minutos. Derecha: tabla con pérdida $.
  const by = area.y + 88;
  drawBarrasH(
    ctx,
    { x: area.x, y: by + 10, w: area.w * 0.42, h: 230 },
    causales.slice(0, 7).map((c) => ({ nombre: c.causal_nombre, minutos: c.minutos_totales })),
    'Sin paros registrados en el período',
  );

  const tx = area.x + area.w * 0.46;
  const tw = area.w * 0.54;
  const filas: Celda[][] = causales.map((c) => [
    {
      texto: c.causal_nombre.length > 26 ? `${c.causal_nombre.slice(0, 25)}…` : c.causal_nombre,
      color: c.perdida_pesos === null ? P.muted : P.text,
    },
    { texto: horasFmt(c.minutos_totales) },
    { texto: `${num1(c.pct_minutos)}%` },
    {
      texto: c.perdida_pesos === null ? '—' : copM(c.perdida_pesos),
      color: c.perdida_pesos === null ? P.faint : P.text,
    },
  ]);
  const perdidaCausales = causales.reduce((s, c) => s + (c.perdida_pesos ?? 0), 0);
  drawTabla(
    ctx,
    { x: tx, y: by, w: tw, h: 0 },
    [
      { titulo: 'Causal', w: tw * 0.42 },
      { titulo: 'Tiempo', w: tw * 0.2, align: 'right' },
      { titulo: '% total', w: tw * 0.15, align: 'right' },
      { titulo: 'Pérdida $', w: tw * 0.23, align: 'right' },
    ],
    filas,
    [
      { texto: `Total (top ${causales.length})`, bold: true },
      { texto: horasFmt(causales.reduce((s, c) => s + c.minutos_totales, 0)), bold: true },
      { texto: `${num1(causales.reduce((s, c) => s + c.pct_minutos, 0))}%`, bold: true },
      { texto: perdidaCausales > 0 ? copM(perdidaCausales) : '—', color: P.text, bold: true },
    ],
    20,
  );
  drawNota(
    ctx,
    area.x,
    PH - 56,
    'Tiempo = paro total registrado (todos los cerrados, incluidos programados). Pérdida "—" = paro programado (no afecta disponibilidad) o sin precio cargado; los programados se miden contra meta en Entrada a Pits.',
  );
  drawFoot(ctx, pagina, total);
  return canvas;
};

const seccionPits: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const area = drawHead(ctx, `${numero} · Entrada a Pits`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    'promedio de minutos por cambio vs meta',
  ]);
  if (!d.pits.ok || d.pits.data.indicadores.length === 0) {
    drawNota(
      ctx,
      area.x,
      area.y + 20,
      'Sin metas de Entrada a Pits configuradas o sin datos del período.',
    );
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const pits = d.pits.data;
  const pitsAnt = d.pitsComp?.ok ? d.pitsComp.data : null;
  const causales = pits.indicadores.slice(0, 2); // 2 columnas como el mockup
  const colW = (area.w - 20) / Math.max(1, causales.length);

  causales.forEach((ind, i) => {
    const cx = area.x + i * (colW + 20);
    ctx.fillStyle = P.zebra;
    roundRect(ctx, cx, area.y, colW, 330, 8);
    ctx.fill();
    ctx.strokeStyle = P.border;
    ctx.stroke();
    // Head: nombre + chip meta.
    ctx.textAlign = 'left';
    ctx.font = `600 14px ${SANS}`;
    ctx.fillStyle = P.text;
    ctx.fillText(ind.causal_nombre, cx + 16, area.y + 24);
    ctx.font = `10px ${MONO}`;
    const chip = `Meta ${num1(ind.meta_minutos_default)} min`;
    const chipW = ctx.measureText(chip).width + 20;
    roundRect(ctx, cx + colW - 16 - chipW, area.y + 12, chipW, 20, 10);
    ctx.strokeStyle = P.warn;
    ctx.stroke();
    ctx.fillStyle = P.warn;
    ctx.fillText(chip, cx + colW - 16 - chipW + 10, area.y + 23);
    // KPI + comparación.
    const prom = ind.promedio_minutos;
    const cumple = ind.cumple_meta;
    ctx.font = `700 34px ${MONO}`;
    ctx.fillStyle = cumple === null ? P.faint : cumple ? P.ok : P.crit;
    ctx.fillText(prom === null ? '—' : num1(prom), cx + 16, area.y + 62);
    const promW = ctx.measureText(prom === null ? '—' : num1(prom)).width;
    const antInd = pitsAnt?.indicadores.find((x) => x.causal_id === ind.causal_id);
    ctx.font = `10px ${SANS}`;
    ctx.fillStyle = P.muted;
    ctx.fillText(
      ` min por cambio · ${miles(ind.eventos_count)} cambios · ${
        cumple === null ? 'sin eventos' : cumple ? 'cumple' : 'no cumple'
      }${
        antInd?.promedio_minutos != null && prom !== null
          ? ` · ant. ${num1(antInd.promedio_minutos)} → ${num1(prom)}`
          : ''
      }`,
      cx + 20 + promW,
      area.y + 64,
    );
    // Tabla de líderes con fila Total.
    const lideres = pits.por_lider
      .filter((l) => l.causal_id === ind.causal_id)
      .sort((a, b) => a.promedio_minutos - b.promedio_minutos)
      .slice(0, 8);
    const tw = colW - 32;
    drawTabla(
      ctx,
      { x: cx + 16, y: area.y + 84, w: tw, h: 0 },
      [
        { titulo: 'Líder', w: tw * 0.5 },
        { titulo: 'Cambios', w: tw * 0.24, align: 'right' },
        { titulo: 'Promedio', w: tw * 0.26, align: 'right' },
      ],
      lideres.map((l) => [
        { texto: `${l.nombre} ${l.apellido}` },
        { texto: miles(l.eventos_count) },
        {
          texto: num1(l.promedio_minutos),
          color: l.promedio_minutos <= ind.meta_minutos_default ? P.ok : P.crit,
          bold: true,
        },
      ]),
      [
        { texto: 'Total causal', bold: true },
        { texto: miles(ind.eventos_count), bold: true },
        {
          texto: prom === null ? '—' : num1(prom),
          color: cumple === null ? P.faint : cumple ? P.ok : P.crit,
          bold: true,
        },
      ],
      20,
    );
  });

  drawNota(
    ctx,
    area.x,
    PH - 56,
    'Metas configurables por línea desde JIDOKAP (Configuración → Metas Entrada a Pits). Promedio = minutos totales / eventos, solo paros cerrados y validados.',
  );
  drawFoot(ctx, pagina, total);
  return canvas;
};

const seccionCostos: DibujaSeccion = (input, numero, pagina, total) => {
  const { canvas, ctx } = crearPagina();
  const d = input.datos;
  const c = d.cumplimiento.ok ? d.cumplimiento.data : null;
  const res = c ? resumenCostosInforme(c) : null;
  const area = drawHead(ctx, `${numero} · Costos`, [
    `${input.tenantNombre} · ${input.plantasLabel}`,
    `${res?.baseLabel ?? 'producción'} · líneas + tiendas · misma fórmula que el dashboard del directivo`,
  ]);
  if (!c || !res) {
    drawNota(
      ctx,
      area.x,
      area.y + 20,
      'Sin datos de costos (verifique el permiso "puede ver costos" y los precios cargados).',
    );
    drawFoot(ctx, pagina, total);
    return canvas;
  }
  const cAnt = d.cumplimientoComp?.ok ? d.cumplimientoComp.data : null;
  const resAnt = cAnt ? resumenCostosInforme(cAnt) : null;
  const lineasOk = d.lineas.ok ? d.lineas.data : null;

  // KPIs CONSOLIDADOS líneas + tiendas — el mismo total que el directivo.
  const kpiW = (area.w - 3 * 14) / 4;
  const kpiDef: Array<[string, number | null | undefined, number | null | undefined]> = [
    ['Valor producido', res.valor_producido, resAnt?.valor_producido],
    ['Costo de mano de obra', res.costo_mano_obra, resAnt?.costo_mano_obra],
    ['Utilidad estimada', res.utilidad, resAnt?.utilidad],
    ['Pérdida por paros', lineasOk?.totales.perdida_pesos_total, null],
  ];
  kpiDef.forEach(([lbl, val, ant], i) => {
    const delta =
      val != null && ant != null && ant !== 0 ? ((val - ant) / Math.abs(ant)) * 100 : null;
    drawKpi(ctx, area.x + i * (kpiW + 14), area.y, kpiW, 74, {
      lbl,
      val: val == null ? '—' : copM(val),
      cmp:
        delta === null
          ? lbl === 'Utilidad estimada' && res.pendiente
            ? `+ ${copM(res.pendiente.utilidad)} pendiente de validar`
            : undefined
          : `ant. ${copM(ant!)} · ${deltaPct(delta)}`,
      cmpTone:
        delta === null
          ? undefined
          : (lbl === 'Costo de mano de obra' ? delta <= 0 : delta >= 0)
            ? 'ok'
            : 'crit',
    });
  });

  if (res.filas.length > 0) {
    const ty = area.y + 100;
    ctx.font = `600 10px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.textAlign = 'left';
    ctx.fillText('UTILIDAD POR ÁREA', area.x, ty);
    const tw = area.w * 0.72;
    drawTabla(
      ctx,
      { x: area.x, y: ty + 8, w: tw, h: 0 },
      [
        { titulo: 'Área', w: tw * 0.28 },
        { titulo: 'Valor producido', w: tw * 0.22, align: 'right' },
        { titulo: 'Mano de obra', w: tw * 0.2, align: 'right' },
        { titulo: 'Utilidad', w: tw * 0.18, align: 'right' },
        { titulo: '% utilidad', w: tw * 0.12, align: 'right' },
      ],
      res.filas.map((a) => [
        { texto: a.nombre },
        { texto: copM(a.valor_producido) },
        { texto: copM(a.costo_mano_obra) },
        { texto: copM(a.utilidad), color: a.utilidad < 0 ? P.crit : P.text, bold: true },
        {
          texto: a.utilidad_pct === null ? '—' : `${num1(a.utilidad_pct)}%`,
          color: a.utilidad_pct !== null && a.utilidad_pct < 0 ? P.crit : P.muted,
        },
      ]),
      [
        { texto: 'Total', bold: true },
        { texto: copM(res.valor_producido), bold: true },
        { texto: copM(res.costo_mano_obra), bold: true },
        { texto: copM(res.utilidad), bold: true, color: res.utilidad < 0 ? P.crit : P.text },
        { texto: res.utilidad_pct === null ? '' : `${num1(res.utilidad_pct)}%`, bold: true },
      ],
      20,
    );
  }
  // Dos notas: base + conciliación validado/pendiente/capturado, y exclusiones.
  drawNota(ctx, area.x, PH - 68, res.nota_base);
  drawNota(ctx, area.x, PH - 44, res.nota_exclusiones);
  drawFoot(ctx, pagina, total);
  return canvas;
};

const DIBUJANTES: Record<SeccionInforme, DibujaSeccion> = {
  resumen: seccionResumen,
  facturacion: seccionFacturacion,
  nivel: seccionNivel,
  productividad: seccionProductividad,
  lideres: seccionLideres,
  paros: seccionParos,
  pits: seccionPits,
  costos: seccionCostos,
};

// =============================================================================
// Entrada pública
// =============================================================================

export async function descargarInformeGerencial(input: InformeGerencialInput): Promise<void> {
  await cargarFuentes();
  const totalPaginas = 1 + input.secciones.length;
  const canvases: HTMLCanvasElement[] = [paginaPortada(input, totalPaginas)];
  input.secciones.forEach((id, i) => {
    const numero = String(i + 1).padStart(2, '0');
    canvases.push(DIBUJANTES[id](input, numero, i + 2, totalPaginas));
  });
  const jpegs = canvases.map((canvas) => ({
    dataUrl: canvas.toDataURL('image/jpeg', 0.92),
    w: PW,
    h: PH,
  }));
  const nombre = `informe-gerencial-${input.periodoLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  descargarPdf(jpegs, nombre);
}
